import { useStytchB2BClient } from '@stytch/react/b2b';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { PageSpinner } from '@/components/loaders';
import { sessionDurationMinutes } from '@/constants/numbers';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import {
  consumeTenantPendingSignInMethod,
  setTenantPreferredSignInMethod,
} from '@/helper/user-preferences';
import useDeferredLoading from '@/hooks/useDeferredLoading';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

type TenantAuthenticateProps = {
  orgSlug: string;
  children: ReactNode;
};

/**
 * Handles tenant-specific (direct) authentication callbacks from Stytch.
 * Unlike DiscoveryAuthenticate which handles discovery flows, this handles:
 * - SSO authentication
 * - OAuth authentication (non-discovery, org-specific)
 * - Multi-tenant magic links
 *
 * These flows return a full session directly without needing an intermediate token.
 */
const TenantAuthenticate = ({ orgSlug, children }: TenantAuthenticateProps) => {
  const [searchParams] = useSearchParams();
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const hasRun = useRef(false);

  const token = searchParams.get('token');
  const stytchTokenType = searchParams.get('stytch_token_type');

  // Initialize authStarted to true if we have a token on first render
  // This persists across URL changes to avoid showing login form during auth
  const [authStarted, setAuthStarted] = useState(() => !!token && !!stytchTokenType);

  // Track if we're processing auth - defer showing spinner to avoid flash
  const showLoading = useDeferredLoading(authStarted);

  const commitTenantPreference = useCallback(() => {
    const pendingMethod = consumeTenantPendingSignInMethod(orgSlug);
    if (pendingMethod) {
      setTenantPreferredSignInMethod(orgSlug, pendingMethod);
    }
  }, [orgSlug]);

  const clearURLSearchParams = useCallback(() => {
    navigate(location.pathname, { replace: true });
  }, [navigate]);

  const handleSSOAuthenticate = useCallback(
    async (token: string) => {
      try {
        await stytch.sso.authenticate({
          sso_token: token,
          session_duration_minutes: sessionDurationMinutes,
        });
        commitTenantPreference();
        navigate(ROUTES.app.path, { replace: true });
      } catch (e) {
        setAuthStarted(false);
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [stytch, navigate, pushNotification, commitTenantPreference],
  );

  const handleOAuthAuthenticate = useCallback(
    async (token: string) => {
      try {
        await stytch.oauth.authenticate({
          oauth_token: token,
          session_duration_minutes: sessionDurationMinutes,
        });
        commitTenantPreference();
        navigate(ROUTES.app.path, { replace: true });
      } catch (e) {
        setAuthStarted(false);
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [stytch, navigate, pushNotification, commitTenantPreference],
  );

  const handleMagicLinksAuthenticate = useCallback(
    async (token: string) => {
      try {
        await stytch.magicLinks.authenticate({
          magic_links_token: token,
          session_duration_minutes: sessionDurationMinutes,
        });
        commitTenantPreference();
        navigate(ROUTES.app.path, { replace: true });
      } catch (e) {
        setAuthStarted(false);
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [stytch, navigate, pushNotification, commitTenantPreference],
  );

  const authenticate = useCallback(async () => {
    if (!token || !stytchTokenType) return;

    clearURLSearchParams();

    switch (stytchTokenType) {
      case 'sso':
        await handleSSOAuthenticate(token);
        break;
      case 'oauth':
        await handleOAuthAuthenticate(token);
        break;
      case 'multi_tenant_magic_links':
        await handleMagicLinksAuthenticate(token);
        break;
      default:
        setAuthStarted(false);
        console.error(`Unsupported authentication type: ${stytchTokenType}`);
    }
  }, [
    token,
    stytchTokenType,
    clearURLSearchParams,
    handleSSOAuthenticate,
    handleOAuthAuthenticate,
    handleMagicLinksAuthenticate,
  ]);

  useEffect(() => {
    if (hasRun.current) return;

    if (token && stytchTokenType) {
      hasRun.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async auth flow needs setState on error
      authenticate();
    }
  }, [token, stytchTokenType, authenticate]);

  // Show spinner only after delay, return null during brief auth to avoid flash
  if (authStarted && showLoading) {
    return <PageSpinner />;
  }

  if (authStarted) {
    return null;
  }

  return <>{children}</>;
};

export default TenantAuthenticate;
